import throttle from 'lodash/throttle';
import { useEffect, useState } from 'react';

import { ApiStatistic } from 'api';
import { CexStrategyFactory } from 'api/cex';
import { BaseStrategy } from 'api/cex/base_strategy';
import { tickSizeToPrecision } from 'api/cex/bootstrap';
import { getMarketPrecision } from 'entities/market';
import { useOrderbookStore } from 'features/cex-orderbook';
import { useLastPriceStore } from 'shared/store';
import { useTypedSelector } from 'store';
import { bn, Bus, humanizeBn, multiplyBignumbers } from 'tools';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { KLine } from 'types/charts';
import { formatFiat } from 'utils/formats/formatFiat';

const formatOrderbookVals = (vals: string[][]) =>
  vals.map(val => {
    const totalBN = multiplyBignumbers([bn(val[0]), 18], [bn(val[1]), 18]);
    const total = humanizeBn(totalBN, 18);

    const totalView = formatFiat(totalBN, 18, '');
    const amount = formatFiat(bn(val[1]), 18, '', { strict: true });

    return {
      price: val[0],
      amount: val[1],
      amountView: amount,
      total,
      totalView,
    };
  });

type UseCexSocketsProps = {
  interval: number;
  orderbookDepth: number;
};

const useCexSockets = ({ interval, orderbookDepth }: UseCexSocketsProps) => {
  const cexPair = useTypedSelector(store => store.pairs.selectedCexPair)!;
  const [strategy, setStrategy] = useState<BaseStrategy | undefined>();

  const updateLastPrice = useLastPriceStore(s => s.update);

  const [isInited, setIsInited] = useState(false);
  const [initialTicker, setInitialTicker] = useState<ITickerSocket>({
    baseVolume: '',
    change: '',
    close: '',
    high: '',
    last: '',
    low: '',
    quoteVolume: '',
  });
  // Chart precision: the strategy's venue value wins; the metrics cache is a
  // fallback once the strategy bootstrap settled without one.
  const [strategyPrecision, setStrategyPrecision] = useState<number | undefined>(undefined);
  const [metricsPrecision, setMetricsPrecision] = useState<number | undefined>(undefined);
  const [bootstrapSettled, setBootstrapSettled] = useState(false);
  const precision = strategyPrecision ?? (bootstrapSettled ? metricsPrecision : undefined);

  useEffect(() => {
    setIsInited(false);
    setStrategy(undefined);
    setStrategyPrecision(undefined);
    setMetricsPrecision(undefined);
    setBootstrapSettled(false);

    let cancelled = false;
    const metricsController = new AbortController();
    const metricsTimer = setTimeout(() => metricsController.abort(), 5000);

    if (cexPair) {
      getMarketPrecision({
        symbol: `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}`,
        cex: cexPair.cex,
        signal: metricsController.signal,
      }).then(res => {
        clearTimeout(metricsTimer);
        const prec = res.isSuccess ? tickSizeToPrecision(res.data?.tick_size) : undefined;
        if (!cancelled && prec !== undefined) setMetricsPrecision(prec);
      });

      const getTicker = () => {
        ApiStatistic.getCexTickerInfo({
          symbol: `${cexPair.token_base.symbol}-${cexPair.token_quote.symbol}`,
          cex: cexPair.cex,
        })
          .then(v => {
            if (v.data && v.isSuccess) {
              const ticker: ITickerSocket = {
                baseVolume: v.data.base_volume,
                quoteVolume: v.data.quote_volume,
                change: v.data.change24h,
                close: v.data.last_price,
                last: v.data.last_price,
                high: v.data.high24h,
                low: v.data.low24h,
              };

              setInitialTicker(ticker);

              // Direct Zustand store update
              useOrderbookStore.getState().updateLastPrice(cexPair.id, ticker.last);

              // Keep Bus.emit and useLastPriceStore for backward compatibility
              Bus.emit(`${cexPair.id}-update-ticker`, ticker);
              updateLastPrice(cexPair.id, { price: ticker.last, isLoading: false });
            }
          })
          .catch(error => {
            console.error(error);
          });
      };

      const setup = async () => {
        const onOrderbookUpdate = throttle(({ bids, asks }: IOrderbook) => {
          // Direct Zustand store update (no Bus events)
          const formattedAsks = formatOrderbookVals(
            asks.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])),
          );
          const formattedBids = formatOrderbookVals(
            bids.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0])),
          );

          useOrderbookStore.getState().updateOrderbook(cexPair.id, {
            asks: formattedAsks.map(a => ({ price: a.price, amount: a.amount })),
            bids: formattedBids.map(b => ({ price: b.price, amount: b.amount })),
          });

          // Keep Bus.emit for backward compatibility with other parts of the app
          Bus.emit(`${cexPair.id}-update-orderbook`, {
            asks: formattedAsks,
            bids: formattedBids,
          });
        }, 300);

        const onTickerUpdate = throttle((ticker: ITickerSocket) => {
          // Direct Zustand store update for last price
          useOrderbookStore
            .getState()
            .updateLastPrice(cexPair.id, ticker.last || ticker.close || '0');

          // Keep Bus.emit and useLastPriceStore for backward compatibility
          Bus.emit(`${cexPair.id}-update-ticker`, ticker);
          updateLastPrice(cexPair.id, { price: ticker.last, isLoading: false });
        }, 1000);

        const klineUpdate = throttle((kline: KLine) => {
          Bus.emit(`${cexPair.id}-update-kline`, kline);
        }, 300);

        const onTradeUpdate = (trade: TradeSocket | null) => {
          Bus.emit(`${cexPair.id}-update-trade`, trade);
        };

        const precisionUpdate = (n: number) => {
          if (!cancelled) setStrategyPrecision(n);
        };

        getTicker();

        let str: BaseStrategy | undefined;
        try {
          str = await CexStrategyFactory({
            orderbookDepth,
            cex: cexPair.cex,
            base: cexPair.token_base.symbol,
            quote: cexPair.token_quote.symbol,
            onOrderbookUpdate,
            onTickerUpdate,
            klineUpdate,
            precisionUpdate,
            onTradeUpdate,
          });
        } catch (error) {
          console.error(error);
        }

        // A pair switch during bootstrap must not attach the old pair's strategy.
        if (cancelled) return;

        setBootstrapSettled(true);
        setStrategy(str);
        setIsInited(true);
      };

      setup();
    }

    return () => {
      cancelled = true;
      clearTimeout(metricsTimer);
      metricsController.abort();
    };

    //eslint-disable-next-line
  }, [cexPair?.id]);

  useEffect(() => {
    if (strategy) {
      strategy?.setOrderbookDepth(orderbookDepth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookDepth]);

  useEffect(() => {
    const handler = () => {
      if (cexPair) {
        // Direct Zustand store update
        useOrderbookStore.getState().updateLastPrice(cexPair.id, initialTicker.last);
        useOrderbookStore.getState().setLoading(cexPair.id, true);

        // Keep Bus.emit and useLastPriceStore for backward compatibility
        updateLastPrice(cexPair.id, { price: initialTicker.last, isLoading: true });
        Bus.emit(`${cexPair.id}-update-ticker`, initialTicker);
      }
    };

    if (cexPair) {
      Bus.on(`${cexPair.id}-ask-ticker`, handler);
    }

    return () => {
      if (cexPair) {
        Bus.off(`${cexPair.id}-ask-ticker`, handler);
      }
    };
  }, [cexPair, initialTicker, updateLastPrice]);

  useEffect(() => {
    if (strategy && isInited) {
      try {
        strategy.connectWebSocket({ interval });
      } catch (error) {
        // A strategy whose bootstrap timed out may lack socket metadata; keep the page alive.
        console.error(error);
      }
    }

    return () => {
      strategy?.closeWebSocket();
    };

    //eslint-disable-next-line
  }, [isInited, interval]);

  return {
    precision,
    strategy,
  };
};

export { useCexSockets };
